import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { TRASH_KINDS, trashApi, type TrashKind, type TrashPage as TrashPageData } from '../api/trash'
import {
  Page,
  PageHeader,
  FilterBar,
  TextField,
  SegmentedButton,
  Callout,
  LoadingState,
  EmptyState,
  Card,
  DataTable,
  Button,
  Pagination,
} from '../components/ui'

/**
 * F-095 (TASK-0349, REQ-287-001): cestino delle anagrafiche. Contatti, persone, aziende, punti di fornitura
 * e lead spostati nel cestino restano in archivio: qui l'amministratore li vede con motivo e data e li
 * ripristina. Nessuna eliminazione definitiva da qui. Pagina solo Admin (il server risponde 403 agli altri).
 */
const PAGE_SIZE = 25

const DETAIL_PATH: Partial<Record<TrashKind, (id: number) => string>> = {
  contacts: id => `/contacts/${id}`,
  companies: id => `/companies/${id}`,
  leads: id => `/leads/${id}`,
  contracts: id => `/contracts/${id}`,
  // F-104: attivita', appuntamenti e spese non hanno una pagina di dettaglio: si apre l'elenco che le conteneva.
  tasks: () => '/operations?tab=tasks',
  appointments: () => '/operations?tab=appointments',
  expenses: () => '/finance/expenses',
}

function fmtDateTime(s: string): string {
  return new Date(s).toLocaleString('it-IT', {
    timeZone: 'Europe/Rome',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function TrashPage() {
  const [kind, setKind] = useState<TrashKind>('contacts')
  const [search, setSearch] = useState('')
  const [pendingSearch, setPendingSearch] = useState('')
  const [page, setPage] = useState(1)
  const [data, setData] = useState<TrashPageData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<number | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setData(await trashApi.list(kind, { search, page, size: PAGE_SIZE }))
    } catch (e) {
      setData(null)
      setError(e instanceof Error ? e.message : 'Errore nel caricamento del cestino')
    } finally {
      setLoading(false)
    }
  }, [kind, search, page])

  useEffect(() => { void load() }, [load])

  function changeKind(next: TrashKind) {
    setKind(next)
    setSearch('')
    setPendingSearch('')
    setPage(1)
    setMessage(null)
  }

  async function restore(id: number) {
    setBusyId(id)
    setError(null)
    setMessage(null)
    try {
      await trashApi.restore(kind, id)
      setMessage('Ripristinato: torna negli elenchi.')
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ripristino non riuscito')
    } finally {
      setBusyId(null)
    }
  }

  const items = data?.items ?? []
  const total = data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const detail = DETAIL_PATH[kind]

  return (
    <Page>
      <PageHeader
        title="Cestino"
        icon="delete"
        count={total}
        actions={
          <SegmentedButton
            aria-label="Tipo di elemento"
            options={TRASH_KINDS.map(k => ({ value: k.kind, label: k.label }))}
            value={kind}
            onChange={v => changeKind(v as TrashKind)}
          />
        }
      />

      <FilterBar
        onSubmit={() => {
          setSearch(pendingSearch)
          setPage(1)
        }}
      >
        <TextField
          label="Cerca per nome o motivo..."
          labelHidden
          placeholder="Cerca per nome o motivo..."
          type="text"
          value={pendingSearch}
          onChange={e => setPendingSearch(e.target.value)}
        />
      </FilterBar>

      {error && <Callout tone="error">{error}</Callout>}
      {message && <Callout tone="success">{message}</Callout>}

      <Card variant="outlined" padding="none">
        {loading ? (
          <LoadingState />
        ) : items.length === 0 ? (
          <EmptyState icon="delete" title="Il cestino è vuoto." />
        ) : (
          <>
            <DataTable variant="plain">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Nel cestino dal</th>
                  <th>Spostato da</th>
                  <th>Motivo</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map(it => (
                  <tr key={it.id}>
                    <td>
                      {detail ? (
                        <Link to={detail(it.id)} className="text-decoration-none md-typescale-emphasized-body-medium">{it.label}</Link>
                      ) : (
                        <span className="md-typescale-emphasized-body-medium">{it.label}</span>
                      )}
                      <small className="text-muted ms-2">#{it.id}</small>
                    </td>
                    <td>{fmtDateTime(it.deletedAt)}</td>
                    <td>{it.deletedBy !== null ? `Utente #${it.deletedBy}` : '—'}</td>
                    <td><small>{it.deleteReason ?? '—'}</small></td>
                    <td className="text-end">
                      <Button
                        variant="outlined"
                       
                        size="sm"
                        disabled={busyId === it.id}
                        onClick={() => void restore(it.id)}
                      >
                        {busyId === it.id ? 'Ripristino...' : 'Ripristina'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
            {totalPages > 1 && <Pagination page={page} pageSize={PAGE_SIZE} total={total} onChange={setPage} />}
          </>
        )}
      </Card>
    </Page>
  )
}
